import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";
import { t } from "ttag";

import type { ColumnsCarouselBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useLazyBackground } from "../../utils/lazyBackground";
import { getSpacingClasses } from "../_spacing/spacing";

type IProps = Immutable<{
    value: Value;
}>;

type Column = NonNullable<Value["columns"]>[number];
type ColumnImage = NonNullable<Column["image"]>;

// With Flickity `contain: true`, 3 slides fit the viewport at the desktop
// breakpoint, so nav buttons are unnecessary and hidden via SCSS .no-buttons.
const HIDE_NAV_BUTTONS_SLIDE_COUNT = 3;

const renderColumnImage = (
    image: Immutable<ColumnImage> | null | undefined,
): React.JSX.Element | null => {
    if (!image) {
        return null;
    }
    const r = image.renditions;
    // Use each rendition's *actual* pixel width for the `w` descriptor. Wagtail
    // never upscales, so e.g. a `width-1000` rendition of a 500px-wide source is
    // really 500px; labeling it `1000w` overstates the file size and makes the
    // browser compute a halved intrinsic width (regressing the image from full-
    // width to a shrunken box). This mirrors the original `wagtail_image` tag,
    // which built its srcset from `r.width`.
    const srcSet = [250, 500, 1000]
        .map((w) => {
            const rendition = r[`width-${w}`];
            return rendition?.url ? `${rendition.url} ${rendition.width}w` : "";
        })
        .filter(Boolean)
        .join(",");
    return (
        <img
            className="responsive-img"
            src={r["width-1000"]?.url ?? image.url}
            srcSet={srcSet || undefined}
            loading="lazy"
            decoding="async"
            alt={image.title}
        />
    );
};

const renderSlideContent = (
    column: Immutable<Column>,
    index: number,
    theme: string | null | undefined,
    ldotGraphicShown: boolean,
): React.JSX.Element => {
    const isLDot = theme === "theme--l-dot";
    const isCardStyle = theme === "theme--card-style-columns";

    const innerContent = (
        <>
            <div>
                {isLDot && (
                    <div
                        className={concatClassNames([
                            "columns-carousel-block__slide-number",
                            ldotGraphicShown
                                ? "columns-carousel-block__slide-number--graphic"
                                : "",
                        ])}
                    >
                        {index + 1}
                    </div>
                )}
                <RichTextBlock value={column.content ?? ""} />
            </div>
            {!isLDot && renderColumnImage(column.image)}
        </>
    );

    return (
        <>
            {isLDot && renderColumnImage(column.image)}
            {isCardStyle ? <div>{innerContent}</div> : innerContent}
        </>
    );
};

export const ColumnsCarouselBlock = (
    props: IProps,
): React.JSX.Element | null => {
    const v = props.value;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const sectionRef = useRef<HTMLElement | null>(null);
    useLazyBackground(sectionRef);
    const columns = v.columns ?? [];
    const totalSlides = columns.length;
    const theme = v.theme?.theme ?? "";
    const noCarousel = v.no_carousel ?? false;
    const bgStyle = v.background_style;
    const bgImage = bgStyle?.background_image;
    const bgImageUrl =
        bgImage?.renditions["width-2000"]?.url ?? bgImage?.url ?? null;
    const ldotGraphicShown = v.ldot_graphic_shown ?? false;
    // These themes put the slide copy on a white card, so the slide opts out
    // of a site's automatic white text on dark section backgrounds (S&F
    // `keeps-text-color`; a no-op elsewhere).
    const hasLightCards =
        theme === "theme--card-style-columns" || theme === "theme--l-dot";

    useEffect(() => {
        // Skip on SSR; Flickity requires window/document
        const win = getWindow();
        if (!win) {
            return;
        }
        if (noCarousel) {
            return;
        }
        const block = sectionRef.current;
        const container = containerRef.current;
        if (!block || !container) {
            return;
        }

        type FlickityInstance = {
            on: (event: string, cb: (...args: unknown[]) => void) => void;
            select: (index: number) => void;
            destroy: () => void;
        };

        let flickity: FlickityInstance | null = null;
        let resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
        let syncTimeoutId: ReturnType<typeof setTimeout> | null = null;
        let cancelled = false;

        // ADA: add carousel markup once Flickity is active.
        const addCarouselMarkup = (): void => {
            block.setAttribute("aria-label", t`carousel`);
            const instructions = block.querySelector<HTMLElement>(
                "[data-columns-carousel-instructions]",
            );
            if (instructions) {
                instructions.setAttribute("aria-hidden", "false");
            }
            const slides = block.querySelectorAll<HTMLElement>(
                ".columns-carousel-block__slide",
            );
            const totalSlides = slides.length;
            slides.forEach((slide, i) => {
                slide.setAttribute("role", "group");
                const slideNumber = i + 1;
                slide.setAttribute(
                    "aria-label",
                    t`Slide ${slideNumber} of ${totalSlides}`,
                );
            });
            const buttonGroup = block.querySelector<HTMLElement>(
                ".columns-carousel-block .buttons",
            );
            if (buttonGroup) {
                buttonGroup.removeAttribute("aria-hidden");
            }
        };

        // ADA: remove carousel markup when Flickity is inactive (e.g. desktop).
        const removeCarouselMarkup = (): void => {
            block.removeAttribute("aria-label");
            const instructions = block.querySelector<HTMLElement>(
                "[data-columns-carousel-instructions]",
            );
            if (instructions) {
                instructions.setAttribute("aria-hidden", "true");
            }
            const slides = block.querySelectorAll<HTMLElement>(
                ".columns-carousel-block__slide",
            );
            slides.forEach((slide) => {
                slide.removeAttribute("role");
                slide.removeAttribute("aria-label");
            });
            const buttonGroup = block.querySelector<HTMLElement>(
                ".columns-carousel-block .buttons",
            );
            if (buttonGroup) {
                buttonGroup.setAttribute("aria-hidden", "true");
            }
        };

        const syncCarouselAriaFromFlickityState = (): void => {
            const isCarouselActive =
                container.classList.contains("flickity-enabled");
            if (isCarouselActive) {
                addCarouselMarkup();
            } else {
                removeCarouselMarkup();
            }
        };

        const buttonGroup = block.querySelector<HTMLElement>(
            ".columns-carousel-block .buttons",
        );

        const onButtonGroupClick = (event: Event): void => {
            if (!flickity || !buttonGroup) {
                return;
            }
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            const buttonEl = target.closest("button");
            if (!(buttonEl instanceof HTMLButtonElement)) {
                return;
            }
            // Only handle direct children of the button group so the index
            // matches the slide index Flickity expects.
            if (buttonEl.parentNode !== buttonGroup) {
                return;
            }
            const index = Array.from(buttonGroup.children).indexOf(buttonEl);
            if (index >= 0) {
                flickity.select(index);
            }
        };

        const onResize = (): void => {
            if (resizeTimeoutId) {
                clearTimeout(resizeTimeoutId);
            }
            resizeTimeoutId = setTimeout(() => {
                resizeTimeoutId = null;
                syncCarouselAriaFromFlickityState();
            }, 100);
        };

        void (async () => {
            const FlickityModule = await import("flickity-imagesloaded");
            if (cancelled) {
                return;
            }
            const Flickity = FlickityModule.default;
            const instance: FlickityInstance = new Flickity(container, {
                watchCSS: true,
                autoPlay: false,
                imagesLoaded: true,
                pageDots: false,
                prevNextButtons: false,
                contain: true,
            });
            flickity = instance;

            instance.on("ready", () => {
                syncCarouselAriaFromFlickityState();
            });
            // watchCSS can deactivate without a new `ready` (e.g. resize to
            // desktop). Sync ARIA state on deactivate too.
            instance.on("deactivate", syncCarouselAriaFromFlickityState);

            if (buttonGroup) {
                buttonGroup.classList.add("is-contain");
                buttonGroup.addEventListener("click", onButtonGroupClick);
                if (
                    buttonGroup.children.length === HIDE_NAV_BUTTONS_SLIDE_COUNT
                ) {
                    buttonGroup.classList.add("no-buttons");
                }
                instance.on("select", (index) => {
                    const i = Number(index);
                    const selectedButton = buttonGroup.children[i];
                    Array.from(buttonGroup.children, (button) => {
                        if (button !== selectedButton) {
                            button.classList.remove("is-selected");
                            button.setAttribute("aria-current", "false");
                        } else {
                            button.classList.add("is-selected");
                            button.setAttribute("aria-current", "true");
                        }
                    });
                });
            }

            win.addEventListener("resize", onResize);
            // One more sync after a delay in case `ready` already fired.
            syncTimeoutId = setTimeout(syncCarouselAriaFromFlickityState, 150);
        })();

        return () => {
            cancelled = true;
            if (resizeTimeoutId) {
                clearTimeout(resizeTimeoutId);
            }
            if (syncTimeoutId) {
                clearTimeout(syncTimeoutId);
            }
            win.removeEventListener("resize", onResize);
            if (buttonGroup) {
                buttonGroup.removeEventListener("click", onButtonGroupClick);
            }
            if (flickity) {
                try {
                    flickity.destroy();
                } catch {
                    // Swallow Flickity teardown errors so unmount stays clean.
                }
                flickity = null;
            }
        };
    }, [noCarousel]);

    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "columns-carousel-block",
                theme,
                bgStyle?.style ?? "",
                v.responsive_visibility?.visibility ?? "",
                bgImageUrl ? "lazy-background" : "",
                v.spacing ? getSpacingClasses(v.spacing) : "",
            ])}
            data-no-carousel={noCarousel ? "true" : "false"}
            data-background-image={bgImageUrl ?? undefined}
        >
            <div>
                <span
                    className="ada-screenreader-only"
                    aria-hidden="true"
                    data-columns-carousel-instructions
                >
                    {t`This is a carousel with slides. Use the slide buttons to move between slides.`}
                </span>
                <header>
                    {v.header_content && (
                        <RichTextBlock value={v.header_content} />
                    )}
                </header>
                <div
                    ref={containerRef}
                    className="columns-carousel-block__container"
                >
                    {/* Index key: ListBlock children don't surface a stable
                        id, so CMS reorders can reuse the wrong slide DOM /
                        Flickity state. Proper fix requires exposing an id
                        from the Python block (via get_api_representation). */}
                    {columns.map((column, index) => (
                        <div
                            key={index}
                            className={concatClassNames([
                                "columns-carousel-block__slide",
                                v.columns_background_style?.style ?? "",
                                hasLightCards ? "keeps-text-color" : "",
                            ])}
                        >
                            {/* Inner group so addCarouselMarkup can still set Slide N of M. */}
                            <div
                                className="columns-carousel-block__slide-item"
                                role="group"
                                aria-label={t`Slide content`}
                            >
                                {renderSlideContent(
                                    column,
                                    index,
                                    theme,
                                    ldotGraphicShown,
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="buttons" aria-hidden="true">
                    {columns.map((_, index) => {
                        const slideNumber = index + 1;
                        return (
                            <button
                                key={index}
                                className={index === 0 ? "is-selected" : ""}
                                aria-current={index === 0 ? "true" : "false"}
                                aria-label={t`Slide ${slideNumber} of ${totalSlides}`}
                            ></button>
                        );
                    })}
                </div>
                {/* Mirror the legacy `{% if value.page_link %}` guard: the
                    button shows whenever a page is chosen, even if its
                    serialized URL is empty (e.g. an unrouteable page in
                    tests). Guarding on `page_link.url` truthiness would drop
                    the button — and ~113px of layout — when the URL is "". */}
                {v.page_link && (
                    <a className="button" href={v.page_link.url}>
                        {t`Learn More`}
                    </a>
                )}
            </div>
        </section>
    );
};
